import React from 'react';
import styled from '@emotion/styled';
import { AppProps, NextWebVitalsMetric } from 'next/app';
import { ApolloProvider } from '@apollo/react-hooks';
import { DesignSystemProvider } from '@zapier/design-system';
import { CurrentAccountIdProvider } from '@zapier/identity';
import { LinkProvider } from '@zapier/common-hooks';
import { useCookieConsent } from '@zapier/cookies';
import { ZapierClient } from 'graphql/clients';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { initDatadogRum } from 'monitoring/datadog';
import { initSentry } from 'monitoring/sentry';
import { GlobalStyles } from 'components/GlobalStyles';
import { mediaQueries } from 'components/mediaQueries';
import { NavigationTracker } from 'components/Navigation/NavigationTracker';
import { SecondaryNavigation } from 'components/SecondaryNavigation/SecondaryNavigation';
import { Link } from 'components/Link/Link';
import { UniversalNavigation } from 'components/Navigation/UniversalNavigation';
import { TrackingProvider } from 'tracking/TrackingProvider';
import {
  GTMBodyScript,
  GTMHeadScript,
} from 'components/GoogleTagManager/GoogleTagManager';
import Head from 'next/head';
import faviconPath from 'utils/faviconPath';
import { ParselyScript } from 'monitoring/ParselyScript';
import { ParselyPageViewTracking } from 'tracking/ParselyPageViewTracking';

initSentry();
const datadogTracker = initDatadogRum();

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (datadogTracker) {
    datadogTracker(metric);
  }
}

type IsArticlePage = { isArticlePage: boolean };

function App({ Component, pageProps, router }: AppProps) {
  const pushRoute = (href: string) => window.location.assign(href);
  const {
    byName: { analytics: analyticsCookies },
  } = useCookieConsent();

  // We need to add styling to the article pages so that the footer isn't buried behind AddThis. To do that, we will
  // check here to see if the page is an article page and pass a prop to emotion to add the styling.
  const isArticlePage = router.pathname === '/blog/[slug]';

  return (
    <React.Fragment>
      <Head>
        <GTMHeadScript />
        <link href="https://fonts.googleapis.com" rel="preconnect" />
        <link href="https://fonts.googleapis.com" rel="dns-prefetch" />
        <link href="https://fonts.gstatic.com" rel="preconnect" />
        <link href="https://fonts.gstatic.com" rel="dns-prefetch" />
        <link href="https://images.ctfassets.net" rel="preconnect" />
        <link href="https://images.ctfassets.net" rel="dns-prefetch" />
        <link href="https://cdn.zapier-staging.com" rel="preconnect" />
        <link href="https://cdn.zapier-staging.com/" rel="dns-prefetch" />
        <link href="https://cdn.zapier.com" rel="preconnect" />
        <link href="https://cdn.zapier.com" rel="dns-prefetch" />
        <link
          href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;800&display=swap"
          rel="stylesheet"
        />
        <link
          rel="alternate"
          type="application/rss+xml"
          title="The Zapier Blog"
          href="https://zapier.com/blog/feeds/latest/"
        />
        {faviconPath() && (
          <link
            rel="icon"
            type="image/x-icon"
            href={`${faviconPath()}/favicon.ico`}
          />
        )}
      </Head>
      <SentryErrorBoundary fallback="An error has occured">
        <ApolloProvider client={ZapierClient}>
          <GlobalStyles />
          <TrackingProvider>
            <NavigationTracker />
            {analyticsCookies ? <ParselyPageViewTracking /> : null}
            <GTMBodyScript />
            <DesignSystemProvider LinkComponent={Link}>
              <CurrentAccountIdProvider>
                <LinkProvider routeTo={pushRoute}>
                  <PageWrapper isArticlePage={isArticlePage}>
                    <UniversalNavigation>
                      <SecondaryNavigation />
                      <Component {...pageProps} />
                      {analyticsCookies && <ParselyScript />}
                    </UniversalNavigation>
                  </PageWrapper>
                </LinkProvider>
              </CurrentAccountIdProvider>
            </DesignSystemProvider>
          </TrackingProvider>
        </ApolloProvider>
      </SentryErrorBoundary>
    </React.Fragment>
  );
}

// This styling adds space at the bottom of the page for the AddThis component on the Article pages only.
const PageWrapper = styled.div`
  padding-bottom: ${(props: IsArticlePage) =>
    props.isArticlePage ? `30px` : `0`};
  ${mediaQueries.desktop} {
    padding-bottom: 0;
  }
`;

export default App;
